import { memo, useState } from 'react';
import { Modal, Alert, Typography } from 'antd';
import { useVisible } from '@wappsto/blanket';
import { Link } from 'react-router-dom';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'translations';
import Timestamp from 'components/helpers/Timestamp';
import InstructionsModal from 'components/guide/InstructionsModal';
import { LINKS } from 'configuration';

interface UsageAlertProps {
  points?: {
    current_period_end?: string;
  };
}

const UsageAlert = memo(({ points = undefined }: UsageAlertProps) => {
  const { t, tC } = useTranslation('features', 'billingAlerts');
  const [optionsVisible, showOptions, hideOptions] = useVisible(false);
  const [instructionsVisible, showInstructions, hideInstructions] = useVisible(false);
  const [instructionsKey, setInstructionsKey] = useState<string | undefined>();

  const handleClick = (value: string) => {
    setInstructionsKey(value);
    showInstructions();
  };

  if (!points) {
    return null;
  }

  return (
    <>
      <Alert
        style={{ backgroundColor: '#ffe8e8' }}
        message={tC('usageAlertTitle')}
        description={
          <>
            {t('usageAlertMessage')}{' '}
            <Link to={LINKS.pricing}>{t('usageAlertNewSubscriptionButton')}</Link>{' '}
            {t('usageAlertMessage2')}{' '}
            <Typography.Link onClick={showOptions}>{t('usageAlertIncreaseLink')}</Typography.Link>.
          </>
        }
        type="error"
        banner
        icon={<WarningOutlined />}
      />
      <Modal
        open={optionsVisible}
        onCancel={hideOptions}
        title={tC('increasePoints.title')}
        className="modal large"
        footer={null}>
        <Typography.Paragraph>
          {t('increasePoints.multipleWays')}{' '}
          <strong>{t('increasePoints.upgradeSubscription')}</strong>{' '}
          {t('increasePoints.otherOptions')}
        </Typography.Paragraph>
        <ul>
          <li>
            <Typography.Title level={5}>{tC('delete.historical.title')}</Typography.Title>
            <Typography.Paragraph>
              {t('delete.historical.everyDevice')}
              <br />
              {t('delete.historical.someData')}{' '}
              <Typography.Link onClick={() => handleClick('INSTRUCTIONS_DISABLE_LOGS')}>
                {t('delete.historical.disableLogs')}
              </Typography.Link>{' '}
              {t('delete.historical.forAll')}{' '}
              <Link onClick={hideOptions} to={LINKS.historicalCleanup}>
                {t('delete.historical.deleteAll')}
              </Link>
              . {t('delete.historical.keepInMind')}
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Title level={5}>{t('delete.device.title')}</Typography.Title>
            <Typography.Paragraph>
              {t('delete.device.everyData')}{' '}
              <Typography.Link onClick={() => handleClick('INSTRUCTIONS_DELETE_DEVICE')}>
                {t('delete.device.deleteIoT')}
              </Typography.Link>
              , {t('delete.device.virtualDevice')}
            </Typography.Paragraph>
          </li>
          <li>
            <Typography.Title level={5}>{tC('delete.renew.title')}</Typography.Title>
            <Typography.Paragraph>
              {t('delete.renew.renew')} <Timestamp timestamp={points.current_period_end} fromNow />{' '}
              (
              <Timestamp timestamp={points.current_period_end} />
              ).
            </Typography.Paragraph>
          </li>
        </ul>
        <br />
        <Typography.Paragraph>
          {t('viewUsageMessage')}{' '}
          <Link onClick={hideOptions} to={LINKS.pointUsage}>
            {t('viewUsageMessageLink')}
          </Link>{' '}
          {t('viewUsageMessage2')}
        </Typography.Paragraph>
      </Modal>
      <InstructionsModal
        instructionsKey={instructionsKey}
        instructionsVisible={instructionsVisible}
        hideInstructions={hideInstructions}
      />
    </>
  );
});

UsageAlert.displayName = 'UsageAlert';

export default UsageAlert;
